import { useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Color, GameState, isGameOver } from '@otrio/engine';
import { LobbyScreen } from './components/LobbyScreen';
import { LobbyRoom } from './components/LobbyRoom';
import { GameScreen } from './components/GameScreen';
import { useGameSocket } from './hooks/useGameSocket';
import { LobbyState, ClientMessage } from './protocol';
import './styles.css';

type Phase = 'initial' | 'lobby' | 'playing' | 'won' | 'draw';

// ─── Session persistence ──────────────────────────────────────────────────────
const SESSION_KEY = 'otrio_session';
interface LocalPlayer { color: Color; name: string; }
interface SessionData { roomCode: string; localPlayers: LocalPlayer[]; }

function saveSession(data: SessionData) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(data));
}
function loadSession(): SessionData | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Migrate old format: { roomCode, color, name } → new format
    if (parsed && typeof parsed.color === 'string') {
      return { roomCode: parsed.roomCode, localPlayers: [{ color: parsed.color, name: parsed.name ?? '' }] };
    }
    return parsed as SessionData;
  } catch { return null; }
}
function clearSession() { localStorage.removeItem(SESSION_KEY); }

function App() {
  const [phase, setPhase] = useState<Phase>('initial');
  const [turnNumber, setTurnNumber] = useState(1);
  const [lobby, setLobby] = useState<LobbyState | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [yourColor, setYourColor] = useState<Color | null>(null);
  const [localColors, setLocalColors] = useState<Color[]>([]);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [isPostGame, setIsPostGame] = useState(false);
  const [peekLobby, setPeekLobby] = useState<LobbyState | null>(null);
  // Holds the session data while an auto-rejoin is in flight
  const pendingRejoinRef = useRef<SessionData | null>(null);

  const { status, error, send } = useGameSocket({
    onConnected() {
      // Auto-rejoin if we have a saved session
      const session = loadSession();
      if (session && session.localPlayers.length > 0) {
        pendingRejoinRef.current = session;
        const primary = session.localPlayers[0];
        send({ type: 'JOIN_ROOM', roomCode: session.roomCode, color: primary.color, name: primary.name });
      }
    },
    onRoomCreated(code, updatedLobby, color) {
      const name = updatedLobby.slots.find(s => s.color === color)?.name ?? '';
      setRoomCode(code);
      setLobby(updatedLobby);
      setYourColor(color);
      setLocalColors([color]);
      saveSession({ roomCode: code, localPlayers: [{ color, name }] });
      setPhase('lobby');
    },
    onRoomJoined(code, updatedLobby, color) {
      const name = updatedLobby.slots.find(s => s.color === color)?.name ?? '';
      setRoomCode(code);
      setLobby(updatedLobby);
      setYourColor(color);
      setPeekLobby(null);

      // Determine full local player list (reconnect vs fresh join)
      const pending = pendingRejoinRef.current;
      const isReconnect = pending?.roomCode === code;
      const allPlayers: { color: Color; name: string }[] = isReconnect
        ? pending!.localPlayers
        : [{ color, name }];
      pendingRejoinRef.current = null;

      setLocalColors(allPlayers.map(lp => lp.color));
      saveSession({ roomCode: code, localPlayers: allPlayers });

      // Claim any additional local colors (beyond the primary)
      for (const lp of allPlayers.slice(1)) {
        send({ type: 'CLAIM_COLOR', roomCode: code, color: lp.color, name: lp.name });
      }

      setPhase('lobby');
    },
    onLobbyPeek(peekedLobby) {
      setPeekLobby(peekedLobby);
    },
    onLobbyUpdated(updatedLobby) {
      setLobby(updatedLobby);
      // Only return to lobby when the server resets gameState (play again) AND
      // we're not in a win/draw state waiting for the user to click Play Again.
      setPhase(prev => {
        if (updatedLobby.gameState === null && prev !== 'lobby' && prev !== 'won' && prev !== 'draw') {
          setIsPostGame(true);
          return 'lobby';
        }
        // Already in lobby and server confirmed reset — clear post-game flag so
        // the normal pre-game UI (Start Game) is shown instead of Play Again.
        if (updatedLobby.gameState === null && prev === 'lobby') {
          setIsPostGame(false);
        }
        return prev;
      });
    },
    onGameStarted(gs, updatedLobby) {
      setGameState(gs);
      setLobby(updatedLobby);
      setPhase('playing');
      setTurnNumber(1);
      setIsPostGame(false);
      // Mid-game reconnect: restore from session
      setYourColor(prev => {
        if (prev) return prev;
        const session = loadSession();
        return session?.localPlayers[0]?.color ?? null;
      });
      setLocalColors(prev => {
        if (prev.length > 0) return prev;
        const session = loadSession();
        return session?.localPlayers.map(lp => lp.color) ?? [];
      });
      setRoomCode(prev => prev ?? updatedLobby.roomCode);

      // If reconnecting, claim any additional local colors
      const pending = pendingRejoinRef.current;
      if (pending) {
        const code = updatedLobby.roomCode;
        for (const lp of pending.localPlayers.slice(1)) {
          send({ type: 'CLAIM_COLOR', roomCode: code, color: lp.color, name: lp.name });
        }
        pendingRejoinRef.current = null;
      }
    },
    onGameUpdated(gs) {
      setGameState(gs);
      if (gs.winner) {
        setPhase('won');
      } else if (isGameOver(gs)) {
        setPhase('draw');
      } else {
        setTurnNumber(t => t + 1);
      }
    },
    onColorClaimed(color, updatedLobby) {
      setLobby(updatedLobby);
      setLocalColors(prev => {
        const next = prev.includes(color) ? prev : [...prev, color];
        const session = loadSession();
        if (session) {
          const name = updatedLobby.slots.find(s => s.color === color)?.name ?? '';
          const filtered = session.localPlayers.filter(lp => lp.color !== color);
          saveSession({ ...session, localPlayers: [...filtered, { color, name }] });
        }
        return next;
      });
    },
  });

  // ─── Create room: enter name + color, then CREATE_ROOM ────────────────────
  function handleCreateRoom(name: string, color: Color) {
    send({ type: 'CREATE_ROOM', name, color });
  }

  function handlePeekRoom(code: string) {
    setPeekLobby(null);
    if (code.length === 5) send({ type: 'PEEK_ROOM', roomCode: code });
  }

  // ─── Join room: enter name + pick a slot ─────────────────────────────────
  function handleJoinRoom(code: string, color: Color, name: string) {
    send({ type: 'JOIN_ROOM', roomCode: code, color, name });
  }

  // ─── Claim an additional color on the same device ────────────────────────
  function handleClaimColor(color: Color, name: string) {
    if (!roomCode) return;
    send({ type: 'CLAIM_COLOR', roomCode, color, name });
  }

  // ─── Forward any in-game or lobby-room message ────────────────────────────
  function handleSendMessage(msg: ClientMessage) {
    send(msg);
  }

  function handlePlayAgain() {
    if (roomCode) {
      send({ type: 'PLAY_AGAIN', roomCode });
      setIsPostGame(true);
      setPhase('lobby');
    } else {
      setPhase('initial');
    }
  }

  function handleExit() {
    if (roomCode) send({ type: 'LEAVE_ROOM', roomCode });
    clearSession();
    setPhase('initial');
    setLobby(null);
    setGameState(null);
    setYourColor(null);
    setLocalColors([]);
    setRoomCode(null);
    setIsPostGame(false);
  }

  // ─── Check for ?join= URL param ──────────────────────────────────────────
  const prefilledCode = new URLSearchParams(window.location.search).get('join') ?? '';

  // ─── Render ───────────────────────────────────────────────────────────────

  if (phase === 'initial') {
    return (
      <LobbyScreen
        status={status}
        prefilledCode={prefilledCode}
        peekLobby={peekLobby}
        onCreateRoom={handleCreateRoom}
        onPeekRoom={handlePeekRoom}
        onJoinRoom={handleJoinRoom}
      />
    );
  }

  if (phase === 'lobby' && lobby && yourColor && roomCode) {
    return (
      <LobbyRoom
        lobby={lobby}
        yourColor={yourColor}
        localColors={localColors}
        roomCode={roomCode}
        onSendMessage={handleSendMessage}
        onClaimColor={handleClaimColor}
        isPostGame={isPostGame}
        onExit={handleExit}
      />
    );
  }

  if (!gameState) return null;

  if (phase === 'playing') {
    return (
      <GameScreen
        gameState={gameState}
        turnNumber={turnNumber}
        yourColors={localColors}
        roomCode={roomCode}
        onSendMessage={send}
        onTurnEnd={() => {}}
        onDraw={() => setPhase('draw')}
        onExit={handleExit}
      />
    );
  }

  if (phase === 'won' && gameState.winner) {
    const winnerPlayer = gameState.players.find(p => p.color === gameState.winner)!;
    return (
      <GameScreen
        gameState={gameState}
        turnNumber={turnNumber}
        yourColors={localColors}
        roomCode={roomCode}
        onSendMessage={send}
        onTurnEnd={() => {}}
        onDraw={() => {}}
        onPlayAgain={handlePlayAgain}
        onExit={handleExit}
      />
    );
  }

  if (phase === 'draw') {
    return (
      <div className="draw-screen">
        <GameScreen
          gameState={gameState}
          turnNumber={turnNumber}
          yourColors={localColors}
          roomCode={roomCode}
          onSendMessage={send}
          onTurnEnd={() => {}}
          onDraw={() => {}}
          suppressDrawOverlay
        />
        <div className="draw-screen-overlay">
          <div className="draw-screen-card">
            <p className="draw-screen-title">Draw!</p>
            <p className="draw-screen-sub">No more moves remaining.</p>
            <button className="start-btn" onClick={handlePlayAgain}>Play Again</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

const container = document.getElementById('root')!;
createRoot(container).render(<App />);

